import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import { Pencil } from 'lucide-react';

import type { DevelopmentDetailDataModel } from '@/domain/models/development.js';
import { InfoRow } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { JiraEpicStatus } from '@/presentation/pages/developments/development-detail/components/jira-epic-status.js';
import { jiraRetryLabel } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import { formatDate } from '@/shared/utils/format-date.js';

interface Props {
    dev: DevelopmentDetailDataModel;
    canOpenEditDialog: boolean;
    onEdit: () => void;
}

export function SummaryTab({ dev, canOpenEditDialog, onEdit }: Props) {
    const { translate } = useApp();
    const epicSync = dev.jiraEpicSync;

    return (
        <Card variant="outlined">
            <CardContent>
                {canOpenEditDialog && (
                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                        <Button size="small" variant="outlined" startIcon={<Pencil size={16} />} onClick={() => onEdit()}>
                            {translate('devDetail.editBtn')}
                        </Button>
                    </Stack>
                )}
                <Stack spacing={0.5}>
                    <InfoRow label={translate('devDetail.title')} value={dev.title} />
                    <InfoRow
                        label={translate('devDetail.jiraEpicKey')}
                        value={
                            <JiraEpicStatus
                                status={epicSync.status}
                                epicKey={dev.jiraEpicKey}
                                epicUrl={dev.jiraEpicUrl}
                                epicStatusName={dev.jiraEpicStatusName}
                                creatingLabel={translate('devDetail.jiraEpicCreating')}
                                retryLabel={jiraRetryLabel(translate, epicSync.nextAttemptAt)}
                                variant="field"
                            />
                        }
                    />
                    <InfoRow label={translate('devDetail.jiraEpicStatus')} value={epicSync.status === 'LINKED' ? dev.jiraEpicStatusName : null} />
                    <InfoRow label={translate('devDetail.description')} value={dev.description} />
                    <InfoRow label={translate('devDetail.project')} value={`${dev.clientName} / ${dev.projectName}`} />
                    <InfoRow label={translate('devDetail.businessProcess')} value={dev.businessProcessName} />
                    <InfoRow label={translate('devDetail.responsibleConsultor')} value={dev.consultorName} />
                    <InfoRow label={translate('devDetail.keyUser')} value={dev.usuarioChaveName} />
                    <InfoRow label={translate('devDetail.techLead')} value={dev.liderTecnicoName} />
                    {/* Gap (T12/T17): backend flat only has linkedDevelopmentId, no displayId/title resolved */}
                    <InfoRow label={translate('devDetail.linkedDev')} value={dev.linkedDevelopmentId} />
                    <InfoRow label={translate('devDetail.category')} value={dev.category ? translate(`devForm.categoryLabels.${dev.category}`) : null} />
                    <InfoRow
                        label={translate('devDetail.functionalComplexity')}
                        value={dev.functionalComplexity ? translate(`complexityLabels.${dev.functionalComplexity}`) : null}
                    />
                    <InfoRow label={translate('devDetail.functionalHours')} value={dev.functionalHours !== null ? `${dev.functionalHours}h` : null} />
                    <InfoRow label={translate('devDetail.complexity')} value={dev.techComplexity ? translate(`complexityLabels.${dev.techComplexity}`) : null} />
                    <InfoRow label={translate('devDetail.estimatedHours')} value={dev.techHours !== null ? `${dev.techHours}h` : null} />
                    <InfoRow label={translate('devDetail.devStartDate')} value={formatDate(dev.startDate)} />
                    <InfoRow label={translate('devDetail.devEndDate')} value={formatDate(dev.devEndDate)} />
                    <InfoRow label={translate('devDetail.createdAt')} value={formatDate(dev.createdAt)} />
                    <InfoRow label={translate('devDetail.lastUpdate')} value={formatDate(dev.updatedAt)} />
                    {dev.cancelReason && <InfoRow label={translate('devDetail.cancelReason')} value={dev.cancelReason} />}
                </Stack>
            </CardContent>
        </Card>
    );
}
